import React, { useEffect, useMemo } from 'react';

import { Controller, UseFormReturn } from 'react-hook-form';

import { useQueryGetFragnetCompletion } from 'api/queries/getFragnetCompletion.query';

import classes from 'components/common/GlobalFiltersInPage/Filters.module.scss';
import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import { OptionValue } from 'components/common/GlobalFiltersInPage/components/WbsFilter/wbsFilter';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { Select } from 'components/DesignSystem/Select';
import { CustomizedFormErrorMessage } from 'components/common';

type Option = {
    id?: number;
    value: string;
    label: string;
};

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: string;
    isSelected?: boolean;
    versionId?: number;
}

export const CompletionPlanSegmentFilter = ({
    name,
    methods,
    filtersStatusRef,
    handleFilters,
    value,
    isSelected = true,
    versionId,
}: Props) => {
    const { data, isSuccess } = useQueryGetFragnetCompletion({
        metaDataId: versionId,
    });

    const options: Option[] = useMemo(() => {
        return (
            data?.map((option) => {
                return {
                    id: option?.id,
                    value: option.name,
                    label: option.label,
                };
            }) ?? []
        );
    }, [data]);

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }

        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, name, filtersStatusRef, isSuccess]);

    const handleChange = (option: ISelectOption<OptionValue> | null) => {
        methods?.setValue(name, option ? option.value : null, { shouldValidate: true });
        handleFilters && handleFilters();
    };

    return isSelected ? (
        <>
            <div data-testid={'battlecards-filters-plan-segment'} className={classes.selectWrapper}>
                <Controller
                    render={({ field }) => (
                        <Select<ISelectOption<string>>
                            id={'battlecards-filters-completion-plan-segment'}
                            {...field}
                            value={options.find((option) => option.value === value) ?? null}
                            onChange={handleChange}
                            options={options}
                            isClearable={true}
                            label={FilterLabels.FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD}
                            isError={Boolean(methods?.formState?.errors[name])}
                        />
                    )}
                    name={name}
                    control={methods?.control}
                    rules={{
                        validate: {
                            activityFragnetValidation: (value) => {
                                return (
                                    !Boolean(!methods?.watch('milestoneFragnet') && value) ||
                                    'Completion Plan Segment filter is missing a value for Fragnet'
                                );
                            },
                        },
                    }}
                />
            </div>
            {methods?.formState?.errors[name] && (
                <CustomizedFormErrorMessage text={methods?.formState?.errors[name]?.message} />
            )}
        </>
    ) : null;
};
